import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useRef, useState } from 'react';

type ImageTargetType = 'primary' | 'alternative';

interface UseAsProductImageDialogProps {
  isOpen: boolean;
  imageUrl: string;
  onClose: () => void;
  onConfirm: (targetType: ImageTargetType, imageBlob: Blob) => void;
}

async function getImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText} (${url})`);
  }
  return response.blob();
}

export const UseAsProductImageDialog: React.FC<UseAsProductImageDialogProps> = ({
  isOpen,
  imageUrl,
  onClose,
  onConfirm,
}) => {
  const [selectedType, setSelectedType] = useState<ImageTargetType>('primary');
  const [loading, setLoading] = useState(false);
  const blobRef = useRef<Blob | null>(null);

  useEffect(() => {
    if (!isOpen || !imageUrl) {
      blobRef.current = null;
      return;
    }
    setLoading(true);
    getImageBlob(imageUrl)
      .then((blob) => {
        blobRef.current = blob;
      })
      .catch((err) => {
        console.error('Failed to fetch image blob:', err);
        blobRef.current = null;
      })
      .finally(() => setLoading(false));
  }, [isOpen, imageUrl]);

  const handleConfirm = useCallback(() => {
    if (blobRef.current) {
      onConfirm(selectedType, blobRef.current);
    }
  }, [onConfirm, selectedType]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold">Use as Product Image</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <CloseIcon />
          </button>
        </div>

        {/* Image Preview */}
        <div className="px-6 pt-4">
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
            <img
              src={imageUrl}
              alt="Embedding preview"
              className="max-h-[350px] w-auto object-contain"
            />
          </div>
        </div>

        {/* Radio Options */}
        <div className="px-6 py-4 space-y-1">
          <label
            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${
              selectedType === 'primary'
                ? 'border-green-600 bg-green-50'
                : 'border-transparent hover:bg-gray-50'
            }`}
            onClick={() => setSelectedType('primary')}
          >
            <input
              type="radio"
              name="image-target"
              checked={selectedType === 'primary'}
              onChange={() => setSelectedType('primary')}
              className="mt-1 accent-green-600"
            />
            <div>
              <p className={`font-medium ${selectedType === 'primary' ? 'text-green-700' : ''}`}>
                Set as primary product image
              </p>
              <p
                className={`text-sm ${selectedType === 'primary' ? 'text-green-600' : 'text-gray-500'}`}
              >
                Replaces current primary
              </p>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border ${
              selectedType === 'alternative'
                ? 'border-purple-600 bg-purple-50'
                : 'border-transparent hover:bg-gray-50'
            }`}
            onClick={() => setSelectedType('alternative')}
          >
            <input
              type="radio"
              name="image-target"
              checked={selectedType === 'alternative'}
              onChange={() => setSelectedType('alternative')}
              className="mt-1 accent-purple-600"
            />
            <div>
              <p
                className={`font-medium ${selectedType === 'alternative' ? 'text-purple-700' : ''}`}
              >
                Add as alternative product image
              </p>
              <p
                className={`text-sm ${selectedType === 'alternative' ? 'text-purple-600' : 'text-gray-500'}`}
              >
                Keeps current primary
              </p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t">
          <button
            onClick={handleConfirm}
            disabled={loading || !blobRef.current}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};
